import React, { memo } from "react";
import { DeleteIcon, RepeatClockIcon } from "@chakra-ui/icons";
import { Box } from "@chakra-ui/layout";
import { HStack, IconButton, SkeletonText } from "@chakra-ui/react";
import { Table, Tbody, Td, Th, Tr, Thead } from "@chakra-ui/table";
import { Tag } from "@chakra-ui/tag";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { CopyText } from "../Utilities/CopyText";
import DateUtil from "../../util/DateUtil";

interface IntegrationsTableProps {
  integrations: CherryHubAdminApi.IntegrationSummaryItem[] | null;
  isLoading: boolean;
  debouncedQuery: string;
  disableActions?: boolean;
  onClickEditIntegration: (
    integration: CherryHubAdminApi.IntegrationSummaryItem
  ) => void;
  onClickDeleteIntegration: (
    integration: CherryHubAdminApi.IntegrationSummaryItem
  ) => void;
  onClickRestoreIntegration: (
    integration: CherryHubAdminApi.IntegrationSummaryItem
  ) => void;
}

export const IntegrationsTable = memo(
  ({
    integrations,
    isLoading,
    disableActions,
    debouncedQuery,
    onClickEditIntegration,
    onClickDeleteIntegration,
    onClickRestoreIntegration,
  }: IntegrationsTableProps) => {
    return (
      <Box w="100%" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th width="10%" whiteSpace="nowrap">
                Integration Id
              </Th>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Last Modified</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {!isLoading &&
              (integrations ?? []).map((integration) => (
                <Tr key={integration.Meta?.Id}>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <CopyText
                      text={integration.Meta?.Id!}
                      copyOnIcon={true}
                      hover={true}
                    >
                      {integration.Meta?.Id}
                    </CopyText>
                  </Td>
                  <Td
                    title="Edit integration."
                    _hover={
                      !integration.Meta?.IsDeleted
                        ? { textDecoration: "underline", cursor: "pointer" }
                        : {}
                    }
                    onClick={() =>
                      !integration.Meta?.IsDeleted &&
                      onClickEditIntegration(integration)
                    }
                  >
                    {!!integration.Meta?.IsDeleted && (
                      <Tag size="sm" mr="2">
                        Deleted
                      </Tag>
                    )}
                    {integration.DisplayName}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <Tag
                      size="sm"
                      bgColor={integration.IsEnabled ? "" : "cherryButton.500"}
                      color={integration.IsEnabled ? "" : "white"}
                    >
                      {integration.IsEnabled ? "Enabled" : "Disabled"}
                    </Tag>
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    {DateUtil.getLocalDateFormatFromString(
                      integration.Meta?.DateCreated
                    )}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    {DateUtil.getLocalDateFormatFromString(
                      integration.Meta?.DateUpdated
                    )}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <HStack spacing={2} justifyContent="center">
                      {(integration.Meta?.IsDeleted && (
                        <IconButton
                          aria-label="Restore integration."
                          title="Restore integration."
                          icon={<RepeatClockIcon />}
                          size="xs"
                          isDisabled={disableActions}
                          onClick={() => onClickRestoreIntegration(integration)}
                        />
                      )) || (
                        <IconButton
                          marginLeft="1"
                          aria-label="Delete integration."
                          title="Delete integration."
                          icon={<DeleteIcon />}
                          size="xs"
                          isDisabled={disableActions}
                          onClick={() => onClickDeleteIntegration(integration)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            {integrations?.length === 0 && !isLoading && (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  No integration found matching query '{debouncedQuery}'
                </Td>
              </Tr>
            )}
            {isLoading && (
              <Tr>
                <Td width="10%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="60%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="10%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="10%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    );
  }
);
